/* eslint-disable no-case-declarations */
/* eslint-disable class-methods-use-this */
import { Injectable } from "@angular/core";
import ZoomVideo, {
    AudioChangeAction,
    ConnectionState,
    MutedSource,
    ParticipantPropertiesPayload,
} from "@zoom/videosdk";
import { API, Logger } from "aws-amplify";
import { Subject } from "rxjs";
import * as Bowser from "bowser";
import { enumToArray } from "@smarterplan/ngx-smarterplan-core";
import { ToastrService } from "ngx-toastr";
import { PEER_VIDEO_STATE_CHANGE_ACTION_TYPE } from "./helpers/listeners.helper";
import { ZoomVideoService } from "./zoom-video.service";
import { ChatParticipant, ChatRecord } from "../types/types.service";
import { ZoomLocalSettingsService } from "./zoom-local-settings.service";
import { ChatMessageType } from "../API.service";
import { ChatMessageService } from "../services/chat-message.service";

@Injectable({
    providedIn: "root",
})
export class ZoomService {
    client = ZoomVideo.createClient();

    private topic = "Salons Visit Scolaire";

    private mediaStream;

    renderedList: any = [];

    zoomConnected = new Subject<boolean>();

    guideConnected = new Subject<boolean>();

    isGuide = false;

    connectedUserCount = new Subject<number>();

    currentUserID: number;

    guideUserID: number;

    nmbConnected: number;

    logger: Logger;

    loggerInfo: { name: string; tourID: string; };

    effectiveZoomState: ConnectionState;

    // ---Safari related---/

    audioDecode: any; // for Safare audio

    audioEncode: any; // for Safari audio

    isSafari = false;

    audioConnected = false;

    // --- Chat related ---/

    chatClient;

    onChatMessagesChange = new Subject<ChatRecord>();

    participants: ChatParticipant[] = [];

    // --- Audio mute/unmute emitter

    onMyMuteChange = new Subject<boolean>();

    onAudioChange = (payload: {
        action: AudioChangeAction;
        source?: MutedSource;
        type?: "phone" | "computer";
    }) => {
        console.log("audio change ", payload);
        if (payload.action === "muted" && payload.source === "active") {
            this.onMyMuteChange.next(true);
        }
        if (payload.action === "unmuted" && payload.source === "active") {
            this.onMyMuteChange.next(false);
        }
        if (payload.action === "join") {
            this.audioConnected = true;
        }
    };

    onChatMessage = async (payload: ChatRecord) => {
        const sender = this.participants.find(
            (_participant) => _participant.userId === payload.sender.userId,
        );
        const receiver = this.participants.find(
            (_participant) => _participant.userId === payload.receiver.userId,
        );
        switch (payload.message) {
            case ChatMessageType.RAISE_HAND:
                // console.log("Hand raised by ", payload.sender.userId);
                sender.isHandRaised = true;
                this.participants.splice(
                    this.participants.indexOf(sender),
                    1,
                    sender,
                );
                // we emit this event in order to update users in museum chat component
                this.connectedUserCount.next(this.participants.length);
                // send toast message to guide
                if (this.isGuide) {
                    this.toastrService.info("✋ Visitor has raised hand!");
                }
                break;
            case ChatMessageType.CANCEL_RAISE_HAND:
                // console.log("Hand raised canceled by", payload.sender.userId);
                sender.isHandRaised = false;
                this.participants.splice(
                    this.participants.indexOf(sender),
                    1,
                    sender,
                );
                // we emit this event in order to update users in museum chat component
                this.connectedUserCount.next(this.participants.length);
                break;
            case ChatMessageType.AUTHORIZE_MICRO:
                if (receiver && this.currentUserID === receiver.userId) {
                    console.log(
                        "Micro authorized for ME",
                        payload.receiver.userId,
                    );
                    // to notify everyone to update their list of participants
                    const zoomChatMessage = await this.chatClient.sendToAll(
                        ChatMessageType.ALLOWED_TO_SPEAK,
                    );
                    if (zoomChatMessage) {
                        this.chatMessageService.saveChatMessageFromZoom(
                            zoomChatMessage,
                            ChatMessageType.ALLOWED_TO_SPEAK,
                        );
                    }
                }
                break;
            case ChatMessageType.REFUSE_MICRO:
                if (receiver && this.currentUserID === receiver.userId) {
                    console.log(
                        "Micro refused for  ME",
                        payload.receiver.userId,
                    );
                    // to notify everyone to update their list of participants
                    const zoomChatMessage = await this.chatClient.sendToAll(
                        ChatMessageType.REFUSED_TO_SPEAK,
                    );
                    if (zoomChatMessage) {
                        this.chatMessageService.saveChatMessageFromZoom(
                            zoomChatMessage,
                            ChatMessageType.REFUSED_TO_SPEAK,
                        );
                    }
                }
                break;
            case ChatMessageType.ALLOWED_TO_SPEAK:
                // console.log("Allowed to speak ", payload.sender.userId);
                sender.isHandRaised = false;
                sender.allowedToSpeak = true;
                this.participants.splice(
                    this.participants.indexOf(sender),
                    1,
                    sender,
                );
                this.connectedUserCount.next(this.participants.length);
                break;
            case ChatMessageType.REFUSED_TO_SPEAK:
                // console.log("Refused to speak ", payload.sender.userId);
                sender.isHandRaised = false;
                sender.allowedToSpeak = false;
                this.participants.splice(
                    this.participants.indexOf(sender),
                    1,
                    sender,
                );
                this.connectedUserCount.next(this.participants.length);
                break;
            // case ChatMessageType.FINISHED_SPEAKING:
            //     sender.isHandRaised = false;
            //     sender.allowedToSpeak = false;
            //     this.participants.splice(
            //         this.participants.indexOf(sender),
            //         1,
            //         sender,
            //     );
            //     this.connectedUserCount.next(this.participants.length);
            //     break;
            case ChatMessageType.TECHNICAL_PROBLEMS:
                sender.technicalProblems = true;
                this.participants.splice(
                    this.participants.indexOf(sender),
                    1,
                    sender,
                );
                this.connectedUserCount.next(this.participants.length);
                // send toast message to guide
                if (this.isGuide) {
                    this.toastrService.info(
                        "Des problèmes techniques sont remontées dans le chat",
                    );
                }
                break;
            default:
                this.onChatMessagesChange.next(payload);
                break;
        }
    };

    onPeerVideoStateChange = (payload: any) => {
        const { action, userId } = payload;
        if (action === PEER_VIDEO_STATE_CHANGE_ACTION_TYPE.Start) {
            this.videoService.toggleParticipantVideo(true, userId);
        } else if (action === "Stop") {
            this.videoService.toggleParticipantVideo(false, userId);
        }
    };

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    onPlayAudioFailed = (_payload: any) => {
        this.logger.error(
            `${JSON.stringify(
                this.loggerInfo,
            )}: auto play failed, waiting user interaction`,
        );
        this.audioConnected = false;
    };

    onUserRemoved = (payload: any) => {
        if (this.isGuide) {
            this.logger.debug(
                `${JSON.stringify(
                    this.loggerInfo,
                )}: User removed ${JSON.stringify(payload)}`,
            );
        }
        this.mergeZoomParticipants();
        this.connectedUserCount.next(this.participants.length);
        this.nmbConnected = this.participants.length;
        // check if the removed user is Guide, then leave
        if (this.guideUserID && payload.length > 0) {
            if (payload[0].userId === this.guideUserID) {
                console.log(
                    "guide left without ending session in inUserRemoved",
                );
                // we wait 5 sec to know if guide left definitely or just reconnecting
                setTimeout(() => {
                    // session should have new host by now
                    const newHost = this.client.getSessionHost();
                    console.log(
                        "Who is Host after timeout?",
                        this.client.getSessionHost(),
                    );
                    if (!newHost) {
                        this.logger.debug(
                            `${JSON.stringify(
                                this.loggerInfo,
                            )}: Guide left and no host since, leaving session.`,
                        );
                        // guide left and did not came back, we leave the session
                        this.guideConnected.next(false);
                        this.zoomConnected.next(false);
                    } else {
                        // guide is back! We need to reconnect to Photon
                        this.logger.debug(
                            `${JSON.stringify(
                                this.loggerInfo,
                            )}: Guide has returned!`,
                        );
                        this.guideConnected.next(true);
                    }
                }, 5000);
            }
        }
    };

    onUserAdded = (payload: any) => {
        if (this.isGuide) {
            this.logger.debug(
                `${JSON.stringify(
                    this.loggerInfo,
                )}: User added ${JSON.stringify(payload)}`,
            );
        }
        this.mergeZoomParticipants();
        this.connectedUserCount.next(this.participants.length);
        this.nmbConnected = this.participants.length;
    };

    onUserUpdated = (payload: Array<ParticipantPropertiesPayload>) => {
        if (payload.length > 0 && this.participants) {
            const userChangedPayload = payload[0];
            // we listen only to mute event
            // eslint-disable-next-line no-prototype-builtins
            if (userChangedPayload.hasOwnProperty("muted")) {
                console.log(
                    "User updated muted",
                    userChangedPayload.userId,
                    " muted: ",
                    userChangedPayload.muted,
                );
                this.mergeZoomParticipants();
                this.connectedUserCount.next(this.participants.length);
            }
        }
    };

    onConnectionChange = (payload: any) => {
        this.logger.debug(
            `${JSON.stringify(
                this.loggerInfo,
            )}: Connection change ${JSON.stringify(payload)}`,
        );
        this.effectiveZoomState = payload.state;
        if (payload.state === ConnectionState.Closed) {
            if (payload.reason === "ended by host") {
                setTimeout(() => {
                    this.logger.info(
                        `${JSON.stringify(
                            this.loggerInfo,
                        )}: host has left, trigerring end meeting`,
                    );
                    this.zoomConnected.next(false);
                }, 2000);
            } else {
                // when session is closed due to idle timeout
                setTimeout(() => {
                    if (
                        this.currentUserID &&
                        this.effectiveZoomState === ConnectionState.Closed
                    ) {
                        this.logger.info(
                            `${JSON.stringify(
                                this.loggerInfo,
                            )}: zoom is off but not in front session)`,
                        );
                        this.zoomConnected.next(false);
                    }
                }, 5000);
            }
        }
    };

    onMediaSdkChange = (payload: any) => {
        const { action, type, result } = payload;
        if (type === "audio" && result === "success") {
            // encode for sending audio stream (talk)
            if (action === "encode") {
                this.audioEncode = true;
            }
            // decode for receiving audio stream (hear)
            else if (action === "decode") {
                this.audioDecode = true;
            }
        }
    };

    constructor(
        private videoService: ZoomVideoService,
        private zoomLocalSettingsService: ZoomLocalSettingsService,
        private chatMessageService: ChatMessageService,
        private toastrService: ToastrService,
    ) {
        /** Listen for the change in the settings */
        this.zoomLocalSettingsService.onMicrophoneChanged.subscribe((id) => {
            if (this.mediaStream) this.mediaStream.switchMicrophone(id);
        });

        this.zoomLocalSettingsService.onSpeakerChanged.subscribe((id) => {
            if (this.mediaStream) this.mediaStream.switchSpeaker(id);
        });

        this.zoomLocalSettingsService.onCameraChanged.subscribe((id) => {
            if (this.mediaStream) this.mediaStream.switchCamera(id);
        });
    }

    // ------- Configuration----------/
    setLogger(logger: Logger, loggerInfo: { name: string; tourID: string; }) {
        this.logger = logger;
        this.loggerInfo = loggerInfo;
    }

    setTopic(sessionID: string) {
        this.videoService.setLogger(this.logger, this.loggerInfo);
        this.topic = sessionID;
    }

    setIsGuide(isGuide: boolean) {
        this.isGuide = isGuide;
    }

    checkIsSafari() {
        const browser = Bowser.getParser(window.navigator.userAgent);
        // console.log(browser.getBrowserName());
        this.isSafari = browser.getBrowserName() === "Safari";
        this.logger.info(
            `${JSON.stringify(
                this.loggerInfo,
            )}: User's browser is ${browser.getBrowserName()}`,
        );
    }

    // ----- Connection related -------------/
    async initClient() {
        if (!this.client) {
            this.client = ZoomVideo.createClient();
        }
        return this.client.init("en-US", "Global");
    }

    async getSignature(): Promise<string> {
        const body = {
            topic: this.topic,
            role_type: this.isGuide ? 1 : 0,
        };
        const response = await API.post("zoomAPI", "/signature", {
            body,
        });
        if (response.signature) {
            return response.signature;
        }
        return null;
    }

    async initAndJoinSession(
        signature: string,
        userName: string,
    ): Promise<void> {
        this.logger.debug(
            `${JSON.stringify(
                this.loggerInfo,
            )}: ======= Joining zoom session =======`,
        );
        this.checkIsSafari();
        return new Promise((resolve, reject) => {
            this.client
                .join(this.topic, signature, userName, "123456", 3)
                .then(() => {
                    this.mediaStream = this.client.getMediaStream();

                    this.videoService.setMediaStream(this.mediaStream);

                    this.chatClient = this.client.getChatClient();
                    this.currentUserID = this.client.getSessionInfo().userId;
                    this.logger.info(
                        `${JSON.stringify(
                            this.loggerInfo,
                        )}: Join meeting success with zoom userID: ${this.currentUserID
                        }`,
                    );
                    this.zoomConnected.next(true);

                    return true;
                })
                .then(() => {
                    this.initClientEventListeners();
                    return true;
                })
                .catch((error) => {
                    return reject(error);
                })
                .finally(() => {
                    return resolve();
                });
        });
    }

    async joinSession(userName: string, sessionID: string): Promise<void> {
        this.setTopic(sessionID);
        const signature = await this.getSignature();
        if (!signature) {
            this.logger.error(
                `${JSON.stringify(this.loggerInfo)}: no zoom signature`,
            );
            return Promise.reject(new Error("no signature"));
        }
        try {
            await this.initClient();
        } catch (error) {
            this.logger.error(
                `${JSON.stringify(
                    this.loggerInfo,
                )}: Error init client ${JSON.stringify(error)}`,
            );
            return Promise.reject(error);
        }
        try {
            await this.initAndJoinSession(signature, userName);
        } catch (error) {
            this.logger.error(
                `${JSON.stringify(
                    this.loggerInfo,
                )}: Error initAndJoinSession ${JSON.stringify(error)}`,
            );
            return Promise.reject(error);
        }
        // this.initParticipants();
        return Promise.resolve();
    }

    async startAudioMuted() {
        // start audio
        this.logger.debug(`${JSON.stringify(this.loggerInfo)}: Starting audio`);
        try {
            if (this.isSafari) {
                // desktop Safari, check if desktop Safari audio has been initialized
                if (this.audioEncode && this.audioDecode) {
                    // desktop Safari audio has been initialized, continue to start audio
                    await this.mediaStream.startAudio();
                } else {
                    // desktop Safari audio has not been initialized, retry or handle error
                    console.log("safari audio has not finished initializing");
                    return;
                }
            } else {
                await this.mediaStream.startAudio();
            }
            this.logger.debug(
                `${JSON.stringify(
                    this.loggerInfo,
                )}: **Starting muted** before is muted: ${this.mediaStream.isAudioMuted()}`,
            );
            // Set the correct micro / speaker / camera according to the settings
            this.mediaStream.switchSpeaker(
                this.zoomLocalSettingsService.speakerDeviceId,
            );
            this.mediaStream.switchMicrophone(
                this.zoomLocalSettingsService.microphoneDeviceId,
            );
            if (this.zoomLocalSettingsService.cameraDeviceId) {
                this.mediaStream.switchCamera(
                    this.zoomLocalSettingsService.cameraDeviceId,
                );
            }
            console.log("Audio, and video device switched");
            this.audioConnected = true;
        } catch (error) {
            this.logger.error(
                `${JSON.stringify(
                    this.loggerInfo,
                )}: Error starting audio ${JSON.stringify(error)}`,
            );
        }
        try {
            // mute audio
            await this.mediaStream.muteAudio();
            this.logger.debug(
                `${JSON.stringify(
                    this.loggerInfo,
                )}: **Result after start muted** is muted: ${this.mediaStream.isAudioMuted()}`,
            );
        } catch (error) {
            this.logger.error(
                `${JSON.stringify(
                    this.loggerInfo,
                )}: Error mute audio ${JSON.stringify(error)}`,
            );
            // if (this.isGuide) {
            //     // rollback for safari
            //     const muted = await this.mediaStream.muteAudio();
            //     console.log("-----muted audio: ", muted);
            // }
        }
    }

    // ------Listeners------//
    initClientEventListeners() {
        this.onUserAddedListener();
        this.onUserRemovedListener();
        this.onPeerVideoStateChangedListener();
        this.onConnectionChangeListener();
        this.onPlayAudioFailedListener();
        this.onChatMessageListener();
        this.onMediaSdkChangeListener();
        this.onUserUpdatedListener();
        this.onAudioChangeListener();
    }

    onAudioChangeListener() {
        this.client.on("current-audio-change", this.onAudioChange);
    }

    onConnectionChangeListener() {
        this.client.on("connection-change", this.onConnectionChange);
    }

    onUserAddedListener() {
        this.client.on("user-added", this.onUserAdded);
    }

    onUserRemovedListener() {
        this.client.on("user-removed", this.onUserRemoved);
    }

    onUserUpdatedListener() {
        this.client.on("user-updated", this.onUserUpdated);
    }

    onPeerVideoStateChangedListener() {
        this.client.on("peer-video-state-change", this.onPeerVideoStateChange);
    }

    onPlayAudioFailedListener() {
        this.client.on("auto-play-audio-failed", this.onPlayAudioFailed);
    }

    onMediaSdkChangeListener() {
        this.client.on("media-sdk-change", this.onMediaSdkChange);
    }

    onChatMessageListener() {
        this.client.on("chat-on-message", this.onChatMessage);
    }

    // ----- Disconnect -------//
    async leaveSession(): Promise<void> {
        this.logger.info(
            `${JSON.stringify(
                this.loggerInfo,
            )}: ======= Leaving session isHost: ${this.client.isHost()}=======`,
        );
        try {
            if (this.isGuide && this.videoService.prevIsSelfVideoOn) {
                this.videoService.cancelLocalVideo();
            }
            await this.client.leave(this.client.isHost());
            this.cleanUp();
            return Promise.resolve();
        } catch (error) {
            this.logger.error(
                `${JSON.stringify(
                    this.loggerInfo,
                )}: Error leave zoom client ${JSON.stringify(error)}`,
            );
            return Promise.reject(error);
        }
    }

    cleanUp() {
        this.audioConnected = false;
        this.mediaStream = null;
        this.videoService.mediaStream = null;
        this.currentUserID = null;
        this.cleanEventHandlers();
        this.client = null;
        this.chatClient = null;
        this.audioDecode = false;
        this.audioEncode = false;
        this.participants = [];
        this.guideUserID = null;
        ZoomVideo.destroyClient();
    }

    cleanEventHandlers() {
        try {
            this.client.off("chat-on-message", this.onChatMessage);
            this.client.off(
                "peer-video-state-change",
                this.onPeerVideoStateChange,
            );
            this.client.off("user-added", this.onUserAdded);
            this.client.off("user-removed", this.onUserRemoved);
            this.client.off("connection-change", this.onConnectionChange);
            this.client.off("auto-play-audio-failed", this.onPlayAudioFailed);
            this.client.off("user-updated", this.onUserUpdated);
            this.client.off("current-audio-change", this.onAudioChange);
        } catch (error) {
            this.logger.error(
                `${JSON.stringify(
                    this.loggerInfo,
                )}: Error unsubscribing zoom listeners ${JSON.stringify(
                    error,
                )}`,
            );
        }
    }

    // ---------Custom Getters ------//
    getChatHistory(): ChatRecord[] {
        const allReservedMessages = enumToArray(ChatMessageType);
        return this.chatClient
            .getHistory()
            .filter((record) => !allReservedMessages.includes(record.message));
    }

    getZoomUserCount(): number {
        return this.nmbConnected;
    }

    async toggleMyMuteUnmuteAudio(toMute: boolean) {
        console.log(`about to ${toMute ? "mute" : "unmute"}  MY audio`);
        if (toMute) {
            await this.videoService.mediaStream.muteAudio();
        } else {
            await this.videoService.mediaStream.unmuteAudio();
        }
    }

    async askToUnmute(userID: number) {
        await this.videoService.mediaStream.unmuteAudio(userID);
    }

    async toggleMuteVisitor(userID: number): Promise<void> {
        try {
            return this.mediaStream.muteAudio(userID);
        } catch (error) {
            console.log(
                `une erreur s'est produite pour l'utilisateur '${userID}' : ${this.mediaStream.muteAudio(
                    userID,
                )}`,
            );
            return Promise.reject(error);
        }
    }

    mergeZoomParticipants() {
        const zoomParticipants = this.client.getAllUser();
        const newList = [];
        for (const zoomParticipant of zoomParticipants) {
            const participant = this.participants.find(
                (p) => p.userId === zoomParticipant.userId,
            );
            if (participant) {
                participant.muted = zoomParticipant.muted;
                if (participant.allowedToSpeak && zoomParticipant.muted) {
                    // update the status if user was allowed to speak
                    participant.allowedToSpeak = false;
                }
                participant.bVideoOn = zoomParticipant.bVideoOn;
                participant.isHost = zoomParticipant.isHost; // if host was disconnected and returned
                newList.push(participant);
            } else {
                newList.push(zoomParticipant);
            }
        }
        this.participants = newList;
        const host = zoomParticipants.find((u) => u.isHost);
        if (host) {
            this.guideUserID = host.userId;
        }
    }

    async initParticipants() {
        this.participants = this.client.getAllUser();
        // update if someone is allowed to speak and his micro is activated
        const messagesStack =
            await this.chatMessageService.getMessagesSinceVisitStart();
        this.participants.forEach((p) => {
            const msgByAuthor = messagesStack
                .filter(
                    (msg) =>
                        msg.authorID === p.displayName &&
                        msg.type !== ChatMessageType.DEFAULT,
                )
                .sort((a, b) => b.timestamp - a.timestamp);
            if (msgByAuthor.length > 0) {
                const lastMsg = msgByAuthor[0];
                switch (lastMsg.type) {
                    case ChatMessageType.ALLOWED_TO_SPEAK:
                        p.allowedToSpeak = true;
                        break;
                    case ChatMessageType.RAISE_HAND:
                        p.isHandRaised = true;
                        break;
                    default:
                        break;
                }
            }
        });

        this.connectedUserCount.next(this.participants.length);
        this.nmbConnected = this.participants.length;
        const guideUser = this.participants.find((user) => user.isHost);
        if (guideUser) {
            this.guideUserID = guideUser.userId;
        }
    }
}
